import Colors from '@dynatrace/strato-design-tokens/colors';

import { getCanvasColor } from '../../../../core/components/canvas/utils/canvas-color.js';
import { drawRoundRect } from '../../../../core/components/canvas/utils/drawRoundRect.js';
import { getOuterCircleSize } from '../../../../core/components/data-point/utils/data-point.js';
import { PointShapeType } from '../../../../core/components/shape/type.js';
import { DEFAULT_HIGHLIGHTED_OPACITY } from '../../../../core/constants.js';
import { DataPoint } from '../../../../core/types/data-point.js';

const TRIANGLE_RADIUS_ADJUSTMENT = 1;
const PILL_BORDER_RADIUS = 7;
const PILL_BORDER_WIDTH = 1;

export const drawPill = (
  ctx: CanvasRenderingContext2D,
  points: DataPoint[],
  strokeColor: string,
) => {
  const { x, y, width, height } = getOuterCircleSize(points, 'y');
  ctx.beginPath();
  drawRoundRect(ctx, x, y, width, height, PILL_BORDER_RADIUS);
  ctx.globalAlpha = DEFAULT_HIGHLIGHTED_OPACITY;
  ctx.fillStyle = getCanvasColor(Colors.Background.Base.Default);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.lineWidth = PILL_BORDER_WIDTH;
  ctx.strokeStyle = getCanvasColor(strokeColor);
  ctx.stroke();
  ctx.closePath();
};

export const drawShapeFill = (
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
  radius: number,
) => {
  drawShape(ctx, point, radius, true, false);
};

export const drawShapeStroke = (
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
  radius: number,
  strokeColor?: string,
) => {
  drawShape(ctx, point, radius, false, true, strokeColor);
};

const createPathCircle = (
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
  radius: number,
) => {
  ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
};

const createPathSquare = (
  radius: number,
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
) => {
  const s = radius;
  ctx.rect(point.x - s, point.y - s, 2 * s, 2 * s);
};

const createPathTriangle = (
  radius: number,
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
) => {
  const r = radius - TRIANGLE_RADIUS_ADJUSTMENT;
  const R = 2 * r;
  const side = r * 2 * Math.sqrt(3);

  ctx.beginPath();
  ctx.moveTo(point.x, point.y - R);
  ctx.lineTo(point.x + side / 2, point.y + r);
  ctx.lineTo(point.x - side / 2, point.y + r);
  ctx.closePath();
};

const createPathDiamond = (
  radius: number,
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
) => {
  const s = radius * Math.sqrt(2);
  ctx.beginPath();
  ctx.moveTo(point.x, point.y - s);
  ctx.lineTo(point.x + s, point.y);
  ctx.lineTo(point.x, point.y + s);
  ctx.lineTo(point.x - s, point.y);
  ctx.closePath();
};

const drawShape = (
  ctx: CanvasRenderingContext2D,
  point: DataPoint & { shape?: PointShapeType },
  radius: number,
  fillShape?: boolean,
  strokeShape?: boolean,
  strokeColor?: string,
) => {
  ctx.beginPath();
  switch (point.shape) {
    case 'circle':
      createPathCircle(ctx, point, radius);
      break;
    case 'square': {
      createPathSquare(radius, ctx, point);
      break;
    }
    case 'triangle': {
      createPathTriangle(radius, ctx, point);
      break;
    }
    case 'diamond': {
      createPathDiamond(radius, ctx, point);
      break;
    }
    default:
      createPathCircle(ctx, point, radius);
  }
  if (fillShape) {
    ctx.fillStyle = getCanvasColor(point.color.Default);
    ctx.fill();
  }
  if (strokeShape) {
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = getCanvasColor(Colors.Background.Base.Default);
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.lineWidth = 1;
    ctx.strokeStyle = getCanvasColor(strokeColor);
    ctx.stroke();
  }
  ctx.closePath();
};
